/**
 * Client for the agent telemetry of release 3.2 (items 3 and 4). Wire shapes
 * mirror apps/api-controller/src/modules/edge/scan-runs.ts and log-pull.ts.
 * Kept out of the large api.ts so parallel work on that file does not collide.
 *
 *   GET  /v1/edge/fleet/:id/scan-runs                       a device's runs, newest first
 *   GET  /v1/edge/fleet/:id/scan-runs/:runId/findings       the findings a run reported
 *   GET  /v1/edge/scan-runs/by-scan/:scanId                 the run a finding's scan id belongs to
 *   POST /v1/edge/fleet/:id/log-requests                    ask the device for its recent log (IT Admin)
 *   GET  /v1/edge/fleet/:id/log-requests                    the device's log requests (IT Admin)
 *   GET  /v1/edge/fleet/:id/log-requests/:rid/content       download one uploaded log (IT Admin)
 */

import { API_BASE, ApiError, apiFetch, getAuthToken, newIdempotencyKey } from './api';

/** The device-management permission the log pull needs (IT Admin). */
export const DEVICE_MANAGE = 'foundational:connector:write';

export interface EdgeScanRunSkipped {
  /** An UnreadReason (too_large, needs_extraction, ...) or not_scanned_by_design. */
  reason: string;
  count: number;
}

/** One finished scan run of a device (release 3.2 telemetry item 3). Null = not reported. */
export interface EdgeScanRun {
  /** The controller's row id (used in URLs). */
  id: string;
  /** The agent's own run id (its run journal). */
  runId: string;
  mode: string | null;
  startedAt: string | null;
  finishedAt: string;
  durationSeconds: number | null;
  /** succeeded | failed | paused */
  outcome: string;
  detail: string | null;
  targetsTotal: number | null;
  targetsFailed: number | null;
  columnsScanned: number | null;
  columnsSkipped: number | null;
  filesSeen: number | null;
  filesScanned: number | null;
  filesPartlyRead: number | null;
  filesSkipped: number | null;
  skipped: EdgeScanRunSkipped[] | null;
  findingsFound: number | null;
  findingsDelivered: number | null;
  findingsUndelivered: number | null;
  cpuAvgPercent: number | null;
  cpuPeakPercent: number | null;
  memPeakMb: number | null;
  cpuCeilingPercent: number | null;
  /** Release 3.6 T-29: database column reads that failed, by class. Absent from an older controller. */
  failedReads?: EdgeScanRunFailedReads | null;
  scanIds: string[];
  receivedAt: string;
}

export interface EdgeScanRunFailedReads {
  total: number;
  byClass: Partial<Record<string, number>>;
  /** At most 20; the counts cover every one. */
  columns?: Array<{ column: string; class: string }>;
}

export interface EdgeScanRunPage {
  runs: EdgeScanRun[];
  nextCursor: string | null;
}

export function listEdgeScanRuns(podRowId: string, cursor?: string | null, limit = 25): Promise<EdgeScanRunPage> {
  const q = new URLSearchParams({ limit: String(limit) });
  if (cursor) q.set('cursor', cursor);
  return apiFetch(`/v1/edge/fleet/${encodeURIComponent(podRowId)}/scan-runs?${q.toString()}`);
}

export interface EdgeScanRunFinding {
  id: string;
  title: string;
  severity: string;
  findingType: string;
  status: string;
  asset: string | null;
}

export interface EdgeScanRunFindings {
  /** false: this device's findings are not keyed by device (a server scanner); counts only. */
  scoped: boolean;
  findings: EdgeScanRunFinding[];
  truncated: boolean;
}

export function listEdgeScanRunFindings(podRowId: string, runRowId: string): Promise<EdgeScanRunFindings> {
  return apiFetch(
    `/v1/edge/fleet/${encodeURIComponent(podRowId)}/scan-runs/${encodeURIComponent(runRowId)}/findings`,
  );
}

export interface EdgeScanRunTrace {
  run: EdgeScanRun;
  device: { id: string; podId: string; displayName: string; hostname: string | null };
}

export function getScanRunForScanId(scanId: string): Promise<EdgeScanRunTrace> {
  return apiFetch(`/v1/edge/scan-runs/by-scan/${encodeURIComponent(scanId)}`);
}

/** A request for a device's recent log (release 3.2 telemetry item 4). Never the content. */
export interface EdgeLogRequest {
  id: string;
  /** requested | uploaded | failed | expired */
  state: string;
  requestedBy: string;
  reason: string;
  maxBytes: number;
  requestedAt: string;
  /** The device must answer by then. */
  expiresAt: string;
  uploadedAt: string | null;
  contentBytes: number | null;
  lineCount: number | null;
  truncated: boolean | null;
  agentRedactions: number | null;
  serverRedactions: number | null;
  agentVersion: string | null;
  sha256: string | null;
  /** The log can be downloaded now. */
  available: boolean;
  contentExpiresAt: string | null;
  failure: string | null;
  downloads: number;
}

export function requestEdgeLog(podRowId: string, input: { reason: string; maxKb?: number }): Promise<{ request: EdgeLogRequest }> {
  return apiFetch(`/v1/edge/fleet/${encodeURIComponent(podRowId)}/log-requests`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: newIdempotencyKey(),
  });
}

export function listEdgeLogRequests(podRowId: string): Promise<{ requests: EdgeLogRequest[] }> {
  return apiFetch(`/v1/edge/fleet/${encodeURIComponent(podRowId)}/log-requests`);
}

/**
 * Download one uploaded log. It needs the Authorization header, so it is
 * fetched here and saved through an object URL (the exportEdgeDevicesCsv
 * pattern). The server audits every download.
 */
export async function downloadEdgeLog(podRowId: string, requestId: string): Promise<{ filename: string }> {
  const token = getAuthToken();
  const headers: Record<string, string> = { accept: 'text/plain' };
  if (token) headers.authorization = `Bearer ${token}`;
  const res = await fetch(
    `${API_BASE}/v1/edge/fleet/${encodeURIComponent(podRowId)}/log-requests/${encodeURIComponent(requestId)}/content`,
    { headers },
  );
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      detail = typeof body?.title === 'string' ? body.title : '';
    } catch {
      detail = `${res.status} ${res.statusText}`;
    }
    throw new ApiError(res.status, detail || `Download failed (${res.status})`, null);
  }
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') ?? '';
  const filename = cd.match(/filename="?([^"]+)"?/i)?.[1] ?? `agent-log-${requestId}.txt`;
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objUrl), 5_000);
  return { filename };
}
